// src/components/Integrations/useIntegrationForms.js

import { useCallback, useEffect, useState } from 'react';
import {
  PROVIDERS,
  listIntegrationForms,
} from './integrationFunctions';

const DEFAULT_PAGE_SIZE = 100;

function mergeFormsById(existingForms = [], incomingForms = []) {
  const merged = new Map();

  existingForms.forEach((form) => {
    if (form?.id || form?.formId) {
      merged.set(form.id || form.formId, form);
    }
  });

  incomingForms.forEach((form) => {
    if (form?.id || form?.formId) {
      merged.set(form.id || form.formId, form);
    }
  });

  return Array.from(merged.values());
}

/**
 * useIntegrationForms
 *
 * Reusable integration form-list hook.
 *
 * Enterprise rule:
 * - Integration modals should receive sanitized form summaries from a
 *   backend-owned callable.
 * - This hook should be reusable for BigQuery now and other providers later.
 * - The hook does not query Firestore directly.
 */
export function useIntegrationForms({
  organizationId,
  provider = PROVIDERS.BIGQUERY,
  enabled = true,
  pageSize = DEFAULT_PAGE_SIZE,
  includeProviderConfig = true,
}) {
  const [forms, setForms] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadInitialForms() {
      if (!enabled || !organizationId || !provider) {
        setForms([]);
        setNextCursor(null);
        setError(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const result = await listIntegrationForms({
          organizationId,
          provider,
          pageSize,
          cursor: null,
          includeProviderConfig,
        });

        if (cancelled) {
          return;
        }

        setForms(Array.isArray(result?.forms) ? result.forms : []);
        setNextCursor(result?.nextCursor || null);

        console.log('[useIntegrationForms] Initial forms loaded:', {
          organizationId,
          provider,
          count: Array.isArray(result?.forms) ? result.forms.length : 0,
          hasNextCursor: Boolean(result?.nextCursor),
        });
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('[useIntegrationForms] Failed to load forms:', err);
        setForms([]);
        setNextCursor(null);
        setError(err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadInitialForms();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    includeProviderConfig,
    organizationId,
    pageSize,
    provider,
    refreshKey,
  ]);

  const loadMore = useCallback(async () => {
    if (!enabled || !organizationId || !provider || !nextCursor) {
      return;
    }

    try {
      setLoadingMore(true);
      setError(null);

      const result = await listIntegrationForms({
        organizationId,
        provider,
        pageSize,
        cursor: nextCursor,
        includeProviderConfig,
      });

      setForms((prev) => mergeFormsById(
        prev,
        Array.isArray(result?.forms) ? result.forms : [],
      ));
      setNextCursor(result?.nextCursor || null);

      console.log('[useIntegrationForms] More forms loaded:', {
        organizationId,
        provider,
        count: Array.isArray(result?.forms) ? result.forms.length : 0,
        hasNextCursor: Boolean(result?.nextCursor),
      });
    } catch (err) {
      console.error('[useIntegrationForms] Failed to load more forms:', err);
      setError(err);
    } finally {
      setLoadingMore(false);
    }
  }, [
    enabled,
    includeProviderConfig,
    nextCursor,
    organizationId,
    pageSize,
    provider,
  ]);

  return {
    forms,
    loading,
    loadingMore,
    error,
    hasMore: Boolean(nextCursor),
    refresh,
    loadMore,
  };
}
